import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Share2, Trash2, Copy as CopyIcon, ExternalLink } from "lucide-react";
import { shares as sharesApi } from "@/lib/api";
import { copyToClipboard } from "@/lib/webShare";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SettingsCard } from "./primitives";

/**
 * SharedListsSection — lists every active share the user has issued,
 * with copy / open / individual-revoke actions plus a multi-select
 * bulk-revoke bar for grooming.
 *
 * State is fully local to this section: `sharesList`, selection Set,
 * per-row revoke target, bulk-revoke confirm. Only depends on
 * `user?.language` for date formatting.
 */
export default function SharedListsSection() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [sharesList, setSharesList] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [selectedShares, setSelectedShares] = useState(() => new Set());
  const [bulkRevoking, setBulkRevoking] = useState(false);
  const [bulkRevokeConfirm, setBulkRevokeConfirm] = useState(false);
  // "Clear untitled" cleanup — counts and confirmation for the affordance
  // that lets a user wipe every accidentally-created empty-title share in
  // one tap. `untitledCount` is derived from the loaded list so the button
  // hides automatically once the list is clean.
  const [clearUntitledConfirm, setClearUntitledConfirm] = useState(false);
  const [clearingUntitled, setClearingUntitled] = useState(false);
  const untitledCount = sharesList.filter((s) => !s.title || !s.title.trim()).length;

  const loadShares = useCallback(async () => {
    setSharesLoading(true);
    try {
      const { data } = await sharesApi.list();
      setSharesList(data?.items || []);
    } catch {
      // Non-critical — the rest of Settings still works.
      setSharesList([]);
    } finally {
      setSharesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const revokeShare = async () => {
    const s = revokeTarget;
    if (!s) return;
    try {
      await sharesApi.revoke(s.share_id);
      setSharesList((prev) => prev.filter((x) => x.share_id !== s.share_id));
      // Drop from any lingering selection so the bulk-count stays accurate.
      setSelectedShares((prev) => {
        if (!prev.has(s.share_id)) return prev;
        const next = new Set(prev);
        next.delete(s.share_id);
        return next;
      });
      toast.success(t("settings.shareRevoked"));
    } catch {
      toast.error(t("settings.shareRevokeFail"));
    } finally {
      setRevokeTarget(null);
    }
  };

  const toggleShareSelected = (share_id) => {
    setSelectedShares((prev) => {
      const next = new Set(prev);
      if (next.has(share_id)) next.delete(share_id);
      else next.add(share_id);
      return next;
    });
  };

  const toggleSelectAllShares = () => {
    setSelectedShares((prev) => {
      if (prev.size === sharesList.length && sharesList.length > 0) return new Set();
      return new Set(sharesList.map((s) => s.share_id));
    });
  };

  const revokeSelectedShares = async () => {
    if (selectedShares.size === 0) return;
    setBulkRevoking(true);
    const ids = Array.from(selectedShares);
    // Revoke in parallel with a small concurrency cap so we don't overwhelm
    // Cloudflare when the user has hundreds of past shares.
    const CONCURRENCY = 4;
    const queue = [...ids];
    const successes = [];
    const failures = [];
    const worker = async () => {
      while (queue.length) {
        const sid = queue.shift();
        try {
          await sharesApi.revoke(sid);
          successes.push(sid);
        } catch {
          failures.push(sid);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker));
    const succSet = new Set(successes);
    setSharesList((prev) => prev.filter((x) => !succSet.has(x.share_id)));
    setSelectedShares(new Set());
    setBulkRevoking(false);
    setBulkRevokeConfirm(false);
    if (failures.length === 0) {
      toast.success(t("settings.sharesRevokedN", { n: successes.length }));
    } else if (successes.length === 0) {
      toast.error(t("settings.sharesRevokeFailN", { n: failures.length }));
    } else {
      toast.success(
        t("settings.sharesRevokedNSome", { n: successes.length, failed: failures.length })
      );
    }
  };

  const copyShareUrl = async (url) => {
    const abs = /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`;
    const ok = await copyToClipboard(abs);
    if (ok) toast.success(t("share.copied"));
  };

  // Bulk-remove every untitled share. Backend endpoint is the source
  // of truth for what counts as "untitled" (null / missing / empty
  // string / whitespace-only) so a legacy row that predates the
  // fallback-title change still gets swept.
  const clearUntitled = async () => {
    setClearingUntitled(true);
    try {
      const { data } = await sharesApi.revokeUntitled();
      const removed = data?.removed || 0;
      setSharesList((prev) => prev.filter((s) => s.title && s.title.trim()));
      setSelectedShares((prev) => {
        // Drop any selected ids that were among the untitled rows so
        // the bulk-count stays accurate after the sweep.
        const next = new Set();
        for (const id of prev) {
          if (sharesList.find((s) => s.share_id === id && s.title && s.title.trim())) {
            next.add(id);
          }
        }
        return next;
      });
      toast.success(t("settings.sharesUntitledCleared", { n: removed }));
    } catch {
      toast.error(t("settings.sharesUntitledClearFail"));
    } finally {
      setClearingUntitled(false);
      setClearUntitledConfirm(false);
    }
  };

  const dateFmt = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(
        user?.language === "es" ? "es-ES" : "en-US",
        { year: "numeric", month: "short", day: "numeric" }
      );
    } catch { return null; }
  };

  return (
    <>
      <SettingsCard
        icon={<Share2 className="w-4 h-4" strokeWidth={1.5} />}
        title={t("settings.sharedListsTitle")}
        help={t("settings.sharedListsHelp")}
      >
        {sharesLoading ? (
          <p className="text-xs text-leaf-mist" data-testid="shares-loading">
            {t("settings.sharesLoading")}
          </p>
        ) : sharesList.length === 0 ? (
          <p className="text-xs text-leaf-mist" data-testid="shares-empty">
            {t("settings.sharesEmpty")}
          </p>
        ) : (
          <>
            {/* Bulk-action bar — visible whenever there's at least one row. */}
            <div
              className="flex items-center justify-between gap-3 mb-2 py-2 px-2 rounded-2xl bg-parchment/40 border border-[#E8E5DF]"
              data-testid="shares-bulk-bar"
            >
              <label className="inline-flex items-center gap-2 text-xs text-leaf-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={sharesList.length > 0 && selectedShares.size === sharesList.length}
                  ref={(el) => {
                    // Indeterminate state (some but not all rows checked)
                    if (el) el.indeterminate = selectedShares.size > 0 && selectedShares.size < sharesList.length;
                  }}
                  onChange={toggleSelectAllShares}
                  data-testid="shares-select-all"
                  className="w-4 h-4 accent-leaf"
                />
                {selectedShares.size === 0
                  ? t("settings.sharesSelectAll")
                  : t("settings.sharesSelectedN", { n: selectedShares.size, total: sharesList.length })}
              </label>
              {selectedShares.size > 0 && (
                <button
                  type="button"
                  onClick={() => setBulkRevokeConfirm(true)}
                  disabled={bulkRevoking}
                  data-testid="shares-bulk-revoke"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E8E5DF] hover:bg-[#FCE9E4] text-rust px-3 py-1.5 text-xs disabled:opacity-60 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t("settings.sharesDeleteN", { n: selectedShares.size })}
                </button>
              )}
              {/* One-tap cleanup — only rendered when there's actually
                  something to clean. Hidden mid-bulk-select so the two
                  destructive affordances don't compete for the click. */}
              {selectedShares.size === 0 && untitledCount > 0 && (
                <button
                  type="button"
                  onClick={() => setClearUntitledConfirm(true)}
                  disabled={clearingUntitled}
                  data-testid="shares-clear-untitled"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E8E5DF] hover:bg-[#FCE9E4] text-rust px-3 py-1.5 text-xs disabled:opacity-60 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t("settings.sharesClearUntitledN", { n: untitledCount })}
                </button>
              )}
            </div>
            <ul className="space-y-2" data-testid="shares-list">
              {sharesList.map((s) => {
                const createdText = dateFmt(s.created_at);
                const expiryText = s.expires_at ? dateFmt(s.expires_at) : t("settings.shareNoExpiry");
                const isChecked = selectedShares.has(s.share_id);
                return (
                  <li
                    key={s.share_id}
                    className={`rounded-2xl border p-3 transition ${
                      isChecked ? "border-leaf/40 bg-[#F0F5EF]" : "border-[#E8E5DF]"
                    }`}
                    data-testid={`share-row-${s.share_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleShareSelected(s.share_id)}
                        aria-label={t("settings.sharesSelectRowAria", { title: s.title || t("settings.shareUntitled") })}
                        data-testid={`share-row-checkbox-${s.share_id}`}
                        className="mt-0.5 w-4 h-4 shrink-0 accent-leaf"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-leaf-ink truncate">
                          {s.title || t("settings.shareUntitled")}
                        </p>
                        <p className="text-[11px] text-leaf-mist mt-0.5">
                          {t("settings.shareMeta", {
                            count: s.scan_count,
                            mode: t(`share.detail.${s.view_mode}Label`),
                            expires: expiryText,
                          })}
                        </p>
                        {createdText && (
                          <p
                            className="text-[11px] text-leaf-mist mt-0.5"
                            data-testid={`share-row-created-${s.share_id}`}
                          >
                            {t("settings.shareCreated", { date: createdText })}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => copyShareUrl(s.share_url)}
                          data-testid={`share-row-copy-${s.share_id}`}
                          aria-label={t("share.copy")}
                          className="w-8 h-8 rounded-full bg-white border border-[#E8E5DF] hover:bg-parchment text-leaf-ink flex items-center justify-center transition"
                        >
                          <CopyIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <a
                          href={/^https?:\/\//i.test(s.share_url) ? s.share_url : `${window.location.origin}${s.share_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`share-row-open-${s.share_id}`}
                          aria-label={t("settings.shareOpen")}
                          className="w-8 h-8 rounded-full bg-white border border-[#E8E5DF] hover:bg-parchment text-leaf-ink flex items-center justify-center transition"
                        >
                          <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(s)}
                          data-testid={`share-row-revoke-${s.share_id}`}
                          aria-label={t("settings.shareRevoke")}
                          className="w-8 h-8 rounded-full bg-white border border-[#E8E5DF] hover:bg-[#FCE9E4] text-rust flex items-center justify-center transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </SettingsCard>

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="revoke-share-title">
              {t("settings.revokeShareTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.revokeShareDesc", { title: revokeTarget?.title || t("settings.shareUntitled") })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-share-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="revoke-share-confirm"
              onClick={revokeShare}
              className="bg-rust hover:bg-rust/90"
            >
              {t("settings.revokeShareConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkRevokeConfirm} onOpenChange={(o) => !o && !bulkRevoking && setBulkRevokeConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="revoke-shares-bulk-title">
              {t("settings.revokeSharesBulkTitle", { n: selectedShares.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.revokeSharesBulkDesc", { n: selectedShares.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-shares-bulk-cancel" disabled={bulkRevoking}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="revoke-shares-bulk-confirm"
              onClick={(e) => { e.preventDefault(); revokeSelectedShares(); }}
              disabled={bulkRevoking}
              className="bg-rust hover:bg-rust/90 font-bold uppercase tracking-wide"
            >
              {bulkRevoking
                ? t("settings.revokingBulk")
                : t("settings.revokeSharesBulkConfirm", { n: selectedShares.size })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearUntitledConfirm} onOpenChange={(o) => !o && !clearingUntitled && setClearUntitledConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="clear-untitled-title">
              {t("settings.clearUntitledTitle", { n: untitledCount })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.clearUntitledDesc", { n: untitledCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-untitled-cancel" disabled={clearingUntitled}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="clear-untitled-confirm"
              onClick={(e) => { e.preventDefault(); clearUntitled(); }}
              disabled={clearingUntitled}
              className="bg-rust hover:bg-rust/90 font-bold uppercase tracking-wide"
            >
              {clearingUntitled
                ? t("settings.clearingUntitled")
                : t("settings.clearUntitledConfirm", { n: untitledCount })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
